import { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, Redirect } from 'wouter';
import { User, LoginResponse } from '../types/auth';
import { login } from '@/api/auth-api';
import { clearAuthStorage, getStoredAuth, setAuthStorage, AUTH_STORAGE_KEYS } from '@/lib/auth-utils';
import JSONbigFactory from "json-bigint";
const JSONbig = JSONbigFactory({ useNativeBigInt: true });

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  /** Update the connected Facebook page in state + localStorage (used after changing page in settings). */
  updateFacebookPage: (page: User['facebookPage']) => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [, setLocation] = useLocation();

  useEffect(() => {
    // Check for stored user data on app initialization
    const { user: storedUser, accessToken } = getStoredAuth();

    if (storedUser && accessToken) {
      try {
        const userData = JSONbig.parse(storedUser);
        setUser(userData);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        // Clear invalid data
        clearAuthStorage();
      }
    }

    setIsLoading(false);
  }, []);

  const handleLogin = async (username: string, password: string) => {
    const response: LoginResponse = await login(username, password);
    const {
      accessToken,
      refreshToken,
      userId,
      username: userUsername,
      email,
      firstName,
      lastName,
      photo,
      state,
      facebookPage,
      store,
    } = response;

    const userData: User = {
      id: String(userId),
      username: userUsername,
      firstName,
      lastName,
      email: email || "",
      name: `${firstName || ""} ${lastName || ""}`.trim(),
      avatar: photo,
      role: state === 1 ? "role_admin" : "role_user",
      isActive: state === 1,
      createdAt: new Date(),
      updatedAt: new Date(),
      facebookPage,
      store,
    };

    setUser(userData);
    setAuthStorage({
      user: JSONbig.stringify(userData),
      accessToken,
      refreshToken,
    });
    setLocation("/dashboard");
  };

  const handleLogout = () => {
    setUser(null);
    clearAuthStorage();
    setLocation('/login');
  };

  const updateFacebookPage = (page: User['facebookPage']) => {
    setUser((prev) => (prev ? { ...prev, facebookPage: page } : prev));
    try {
      const { user: storedUser } = getStoredAuth();
      if (storedUser) {
        const parsed = JSONbig.parse(storedUser);
        parsed.facebookPage = page;
        localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSONbig.stringify(parsed));
      }
    } catch (error) {
      console.error('Error updating stored facebook page:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login: handleLogin,
        logout: handleLogout,
        updateFacebookPage,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Protected route component
export function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return <Redirect to="/login" />;

  return <>{children}</>;
}
